/**
 * /me — advisor's personal scheduler view.
 *
 * Authenticated advisors land here from the portal's Scheduler tab.
 * Surfaces in PR #30 + PR #30.5 + PR #30.6:
 *   1. Per-meeting-type scheduling links — one card per active
 *      meeting_type (1st, 2nd, 3rd, 4th, 5th+, Anniversary, Application).
 *      Each card has Copy + Preview buttons. URL shape:
 *        /<slug>                  → 1st meeting (default)
 *        /<slug>/<mt-slug>        → that specific meeting type
 *      Mirrors ChiliPiper's meeting-types page UX so the muscle memory
 *      transfers.
 *   2. "Book on behalf" launcher — opens a modal that lets the advisor
 *      (or admin) book a meeting directly onto their calendar without
 *      going through the public qualifying form. Used for follow-ups
 *      captured by phone, anniversary reviews, etc.
 *   3. Admin preview mode (PR #30.6) — when a pure admin (no advisor
 *      record of their own, e.g. Zach) lands here, they can pick any
 *      advisor from a dropdown and see EXACTLY what that advisor sees
 *      on their own /me. URL shape: /me?as=<slug>. A small banner makes
 *      it obvious you're previewing, not in your own view, so Copy
 *      buttons still copy the prospect-facing URL for the right advisor.
 *
 * Future surfaces (V3+):
 *   - Upcoming bookings list pulled from /api/me/bookings
 *   - Recent reschedule/cancel activity
 *   - Operational alerts (no-shows, awaiting follow-up)
 */

import { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { BookOnBehalfModal } from '../components/BookOnBehalfModal';
import { WorkingHoursEditor } from '../components/WorkingHoursEditor';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import { authedFetch } from '../lib/authed-fetch';
import '../admin.css';
// BookOnBehalfModal pulls in its own CSS + App.css for the SlotPicker.
// We re-import the modal's stylesheet here so the "Book a meeting →"
// button on /me uses the shared .bob-primary-btn styling even before
// the modal mounts.
import '../components/BookOnBehalfModal.css';

const SCHEDULER_BASE =
  typeof window !== 'undefined' && window.location.hostname.endsWith('annuityassociation.com')
    ? 'https://scheduler.annuityassociation.com'
    : window.location.origin;

const PORTAL_BASE = 'https://aaportal.annuityassociation.com';

interface MeAdvisor {
  id: string;
  slug: string | null;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  title: string | null;
  bio: string | null;
  phone: string | null;
  headshotFilename: string | null;
  isActive: boolean;
}

interface MeetingTypeRow {
  id: string;
  slug: string;
  displayName: string;
  displayOrder: number;
}

type AdvisorState =
  | { status: 'loading' }
  | { status: 'not_advisor' }
  | { status: 'error'; message: string }
  | { status: 'ok'; advisor: MeAdvisor };

/**
 * Subtitle copy under each meeting-type card. Lets the advisor know at a
 * glance when they'd typically share each link with a prospect.
 */
const MEETING_TYPE_SUBTITLES: Record<string, string> = {
  '1st-meeting':
    'For brand-new prospects who already know they want to meet with you.',
  '2nd-meeting':
    'For prospects between their 1st and 2nd appointments.',
  '3rd-meeting':
    'For prospects who need a 3rd touchpoint before a decision.',
  '4th-meeting':
    'For prospects who need additional follow-up before applying.',
  '5th-plus-meeting':
    'Catch-all for any further follow-up beyond the 4th meeting.',
  'annuity-anniversary':
    'Annual review for existing clients on their policy anniversary.',
  'application-meeting':
    'For clients ready to walk through and submit the application.',
  'annuity-policy-delivery-review':
    'Walk a new client through their issued policy together at delivery.',
};

interface AdvisorListItem {
  id: string;
  fullName: string;
  email: string;
  slug: string | null;
  isActive: boolean;
}

/**
 * Read the optional ?as=<slug> query param so admins can deep-link
 * to a specific advisor's preview. Treat empty/whitespace as null.
 */
function getInitialPreviewSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('as');
  return v && v.trim() ? v.trim().toLowerCase() : null;
}

export function AdvisorMe() {
  const state = useCurrentUser();
  const [advisorState, setAdvisorState] = useState<AdvisorState>({ status: 'loading' });
  const [meetingTypes, setMeetingTypes] = useState<MeetingTypeRow[]>([]);
  const [showBob, setShowBob] = useState(false);
  // Per-card "Copied ✓" state — keyed by meeting-type slug so each card
  // has its own indicator that doesn't flash on its neighbors.
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  // Admin preview mode (PR #30.6).
  // - previewSlug: the advisor whose /me layout we're previewing (null
  //   = view our own). Initialized from ?as=<slug> so admins can
  //   deep-link or share a specific preview URL.
  // - advisorList: roster for the dropdown picker. Only loaded when
  //   the calling user is admin.
  const [previewSlug, setPreviewSlug] = useState<string | null>(getInitialPreviewSlug);
  const [advisorList, setAdvisorList] = useState<AdvisorListItem[]>([]);

  // Redirect unauthenticated visitors to the portal's login screen.
  useEffect(() => {
    if (state.status === 'unauthenticated') {
      window.location.href = redirectToPortalLogin();
    }
  }, [state.status]);

  // Admin-only: load the active advisor roster for the preview picker.
  useEffect(() => {
    if (state.status !== 'authenticated' || !state.user.isAdmin) return;
    let cancelled = false;
    authedFetch('/api/admin/advisors')
      .then(async (r) =>
        r.ok ? ((await r.json()) as { ok: true; advisors: AdvisorListItem[] }) : null
      )
      .then((res) => {
        if (cancelled || !res || !res.ok) return;
        const active = res.advisors.filter((a) => a.isActive);
        setAdvisorList(active);
        // If there's no ?as= yet AND the admin doesn't have their own
        // advisor record (Zach's case), default the preview to the
        // first advisor alphabetically so the page isn't empty.
        setPreviewSlug((current) => {
          if (current) return current;
          if (state.user.advisorId) return null; // they have their own — show that
          const sorted = [...active].sort((a, b) => a.fullName.localeCompare(b.fullName));
          return sorted[0]?.slug ?? null;
        });
      })
      .catch(() => {
        /* non-fatal — admins can still view their own /me without the picker */
      });
    return () => {
      cancelled = true;
    };
  }, [state.status, state.status === 'authenticated' ? state.user.isAdmin : false, state.status === 'authenticated' ? state.user.advisorId : null]);

  // Keep the URL ?as= param in sync with the picker so a refresh keeps
  // the same preview state (and the admin can share a deep link).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (previewSlug) {
      url.searchParams.set('as', previewSlug);
    } else {
      url.searchParams.delete('as');
    }
    window.history.replaceState({}, '', url.toString());
  }, [previewSlug]);

  // Load the meeting-type catalog so the card grid can render one tile
  // per active type. We hit the auth-protected templates endpoint (the
  // /me page is itself auth-gated, so this is safe). Fallback to a
  // hardcoded list if the endpoint is unavailable so the page still
  // shows something useful.
  useEffect(() => {
    if (state.status !== 'authenticated') return;
    let cancelled = false;
    authedFetch('/api/templates/meeting-types')
      .then(async (r) =>
        r.ok ? ((await r.json()) as { ok: true; meetingTypes: MeetingTypeRow[] }) : null
      )
      .then((res) => {
        if (cancelled || !res || !res.ok) return;
        setMeetingTypes(
          [...res.meetingTypes].sort(
            (a, b) => a.displayOrder - b.displayOrder || a.slug.localeCompare(b.slug)
          )
        );
      })
      .catch(() => {
        if (cancelled) return;
        // Fallback list — display order matches migration 0010 seed.
        setMeetingTypes([
          { id: '', slug: '1st-meeting', displayName: '1st Meeting', displayOrder: 1 },
          { id: '', slug: '2nd-meeting', displayName: '2nd Meeting', displayOrder: 2 },
          { id: '', slug: '3rd-meeting', displayName: '3rd Meeting', displayOrder: 3 },
          { id: '', slug: '4th-meeting', displayName: '4th Meeting', displayOrder: 4 },
          { id: '', slug: '5th-plus-meeting', displayName: 'Follow-up Meeting', displayOrder: 5 },
          { id: '', slug: 'application-meeting', displayName: 'Application Meeting', displayOrder: 6 },
          { id: '', slug: 'annuity-anniversary', displayName: 'Annuity Anniversary', displayOrder: 7 },
        ]);
      });
    return () => {
      cancelled = true;
    };
  }, [state.status]);

  // Load the advisor record powering the page. Two paths:
  //   - Preview mode (admin picked someone else): hit the public
  //     /api/advisor-by-slug endpoint with the picked slug. No auth
  //     needed — that endpoint is public.
  //   - Self mode: hit /api/me-advisor (auth-gated, returns the
  //     calling user's own advisor row). Pure admins (no advisor_id)
  //     get not_advisor and fall through to the empty-state UX.
  useEffect(() => {
    if (state.status !== 'authenticated') return;

    // PREVIEW MODE — admin viewing someone else's /me.
    if (previewSlug) {
      let cancelled = false;
      setAdvisorState({ status: 'loading' });
      fetch(`/api/advisor-by-slug?slug=${encodeURIComponent(previewSlug)}`)
        .then(async (r) => {
          const payload = (await r.json().catch(() => null)) as
            | { ok: true; advisor: { id: string; slug: string; fullName: string; firstName: string; lastName: string; title: string | null; bio: string | null; phone: string | null; headshotFilename: string | null } }
            | { ok: false; error: string; message: string }
            | null;
          if (cancelled) return;
          if (!payload) {
            setAdvisorState({ status: 'error', message: 'Network error' });
            return;
          }
          if (!payload.ok) {
            setAdvisorState({
              status: 'error',
              message: payload.message || 'Could not load advisor.',
            });
            return;
          }
          // Synthesize a MeAdvisor — /api/advisor-by-slug doesn't return
          // email or isActive, but we don't render those in preview mode.
          // (BookOnBehalf advisor dropdown lookup is keyed by id and uses
          // its own /api/admin/advisors fetch.)
          setAdvisorState({
            status: 'ok',
            advisor: {
              id: payload.advisor.id,
              slug: payload.advisor.slug,
              fullName: payload.advisor.fullName,
              firstName: payload.advisor.firstName,
              lastName: payload.advisor.lastName,
              email: '',
              title: payload.advisor.title,
              bio: payload.advisor.bio,
              phone: payload.advisor.phone,
              headshotFilename: payload.advisor.headshotFilename,
              isActive: true,
            },
          });
        })
        .catch(() => {
          if (cancelled) return;
          setAdvisorState({ status: 'error', message: 'Network error' });
        });
      return () => {
        cancelled = true;
      };
    }

    // SELF MODE — view your own /me.
    if (!state.user.advisorId) {
      setAdvisorState({ status: 'not_advisor' });
      return;
    }
    let cancelled = false;
    setAdvisorState({ status: 'loading' });
    authedFetch('/api/me-advisor')
      .then(async (r) => {
        const payload = (await r.json().catch(() => null)) as
          | { ok: true; advisor: MeAdvisor }
          | { ok: false; error: string; message: string }
          | null;
        if (cancelled) return;
        if (!payload) {
          setAdvisorState({ status: 'error', message: 'Network error' });
          return;
        }
        if (!payload.ok) {
          if (payload.error === 'not_advisor') {
            setAdvisorState({ status: 'not_advisor' });
          } else {
            setAdvisorState({ status: 'error', message: payload.message });
          }
          return;
        }
        setAdvisorState({ status: 'ok', advisor: payload.advisor });
      })
      .catch(() => {
        if (cancelled) return;
        setAdvisorState({ status: 'error', message: 'Network error' });
      });
    return () => {
      cancelled = true;
    };
  }, [state.status, state.status === 'authenticated' ? state.user.advisorId : null, previewSlug]);

  if (state.status === 'loading' || advisorState.status === 'loading') {
    return (
      <div className="admin-shell">
        <p style={{ textAlign: 'center', padding: 64, color: '#8C8C8C' }}>Loading…</p>
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    // useEffect above is redirecting — render nothing in the meantime.
    return null;
  }

  const { user } = state;
  const role = user.isAdmin
    ? user.advisorId
      ? 'Admin · Advisor'
      : 'Admin'
    : user.advisorId
      ? 'Advisor'
      : 'Team';

  const advisor = advisorState.status === 'ok' ? advisorState.advisor : null;
  const personalLink = advisor && advisor.slug
    ? `${SCHEDULER_BASE}/${advisor.slug}`
    : null;
  // Header greeting reflects who's signed in, NOT who's being previewed.
  // In self mode we use the advisor's first name; in admin preview mode
  // (or when there's no own-advisor record) we fall back to the email
  // local-part so the greeting still says "Welcome, Zach" when Zach is
  // signed in and previewing Tom.
  const ownFirstName =
    !previewSlug && advisor ? advisor.firstName : null;
  const emailLocal = user.email.split('@')[0] ?? '';
  const emailFirst = emailLocal.split(/[._-]/)[0] ?? emailLocal;
  const displayName =
    ownFirstName ?? (emailFirst.charAt(0).toUpperCase() + emailFirst.slice(1).toLowerCase());

  // Self-advisor option for the modal — locked when the user is a
  // non-admin advisor. Admins without their own advisor row pass null
  // and get the dropdown of all advisors.
  const selfAdvisorOption =
    advisor != null
      ? {
          id: advisor.id,
          fullName: advisor.fullName,
          firstName: advisor.firstName,
          email: advisor.email,
          isActive: advisor.isActive,
          slug: advisor.slug,
        }
      : null;

  function buildMeetingTypeUrl(meetingTypeSlug: string): string | null {
    if (!advisor || !advisor.slug) return null;
    // 1st-meeting is the implicit default — its link is the bare /<slug>
    // for cleaner shareable URLs and backwards compatibility with anyone
    // who already saved the original personal link.
    return meetingTypeSlug === '1st-meeting'
      ? `${SCHEDULER_BASE}/${advisor.slug}`
      : `${SCHEDULER_BASE}/${advisor.slug}/${meetingTypeSlug}`;
  }

  function copyMeetingTypeUrl(meetingTypeSlug: string) {
    const url = buildMeetingTypeUrl(meetingTypeSlug);
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedSlug(meetingTypeSlug);
        window.setTimeout(() => {
          setCopiedSlug((current) => (current === meetingTypeSlug ? null : current));
        }, 1500);
      })
      .catch(() => {
        /* clipboard blocked — silent fail */
      });
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <Logo className="admin-logo" />
        <div className="admin-header-text">
          <h1>Welcome, {displayName}</h1>
          <p className="admin-sub">
            {role} · {user.email} ·{' '}
            <a href={`${PORTAL_BASE}/dashboard`}>Open portal ↗</a>
          </p>
        </div>
      </header>

      <main className="admin-main">
        {/* ----- Admin preview banner + advisor picker ----- */}
        {user.isAdmin && advisorList.length > 0 && (
          <section className="admin-preview-banner">
            <div className="admin-preview-banner-text">
              <strong>Admin preview</strong>
              <span>
                {previewSlug
                  ? `Viewing what ${advisor?.firstName ?? previewSlug} sees on /me. Copy buttons copy that advisor's prospect-facing URLs, not yours.`
                  : 'Pick an advisor to preview their personal scheduling page exactly as they see it.'}
              </span>
            </div>
            <div className="admin-preview-banner-controls">
              <label htmlFor="admin-preview-picker" className="admin-preview-banner-label">
                View as
              </label>
              <select
                id="admin-preview-picker"
                value={previewSlug ?? ''}
                onChange={(e) => setPreviewSlug(e.target.value || null)}
              >
                {user.advisorId && (
                  <option value="">(myself)</option>
                )}
                {!user.advisorId && !previewSlug && (
                  <option value="" disabled>— pick an advisor —</option>
                )}
                {[...advisorList]
                  .sort((a, b) => a.fullName.localeCompare(b.fullName))
                  .map((a) => (
                    <option key={a.id} value={a.slug ?? ''} disabled={!a.slug}>
                      {a.fullName}
                      {!a.slug ? ' (no slug)' : ''}
                    </option>
                  ))}
              </select>
            </div>
          </section>
        )}

        {/* ----- Personal scheduling links — one card per meeting type ----- */}
        {advisorState.status === 'ok' && personalLink && meetingTypes.length > 0 && (
          <section className="admin-card">
            <h2>
              {previewSlug
                ? `${advisor!.firstName}'s personal scheduling links`
                : 'Your personal scheduling links'}
            </h2>
            <p className="admin-card-desc">
              {previewSlug
                ? `These are the links ${advisor!.firstName} can share with prospects who already know they want to meet with them. Each link routes straight onto ${advisor!.firstName}'s calendar for that specific meeting type — no round-robin, no qualifying form.`
                : 'Share these with prospects who already know they want to meet with you. Each link goes straight onto your calendar for that specific meeting type — no round-robin, no qualifying form. Use the right link for the moment: a 1st appointment for a brand-new prospect, a 2nd or 3rd for follow-ups, an Application or Anniversary link when it\'s time to apply or review.'}
            </p>
            <div className="me-link-grid">
              {meetingTypes.map((mt) => {
                const url = buildMeetingTypeUrl(mt.slug);
                if (!url) return null;
                const subtitle = MEETING_TYPE_SUBTITLES[mt.slug] ?? '';
                const isCopied = copiedSlug === mt.slug;
                return (
                  <article key={mt.slug} className="me-link-card">
                    <header className="me-link-card-head">
                      <h3>{mt.displayName}</h3>
                      {mt.slug === '1st-meeting' && (
                        <span className="me-link-card-badge">DEFAULT</span>
                      )}
                    </header>
                    {subtitle && <p className="me-link-card-desc">{subtitle}</p>}
                    <p className="me-link-card-url">
                      <code>{url}</code>
                    </p>
                    <div className="me-link-card-actions">
                      <button
                        type="button"
                        className="admin-copy-btn"
                        onClick={() => copyMeetingTypeUrl(mt.slug)}
                      >
                        {isCopied ? 'Copied ✓' : 'Copy link'}
                      </button>
                      <a
                        className="admin-copy-btn admin-copy-btn-secondary"
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Preview ↗
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
            <p className="admin-card-note">
              {previewSlug
                ? <>Slug: <code>{advisor!.slug}</code>. Slug edits go through admin to keep saved links from breaking for anyone who's already received them.</>
                : <>Your slug is <code>{advisor!.slug}</code>. To change it, ask Zach — slug edits go through admin to keep saved links from breaking for anyone who's already received them.</>}
            </p>
          </section>
        )}

        {advisorState.status === 'ok' && !personalLink && (
          <section className="admin-card">
            <h2>Personal scheduling links</h2>
            <p className="admin-card-desc">
              You don't have a personal slug yet. Ask Zach to assign one —
              he can do it from the admin dashboard. Once set, your links
              will be <code>{SCHEDULER_BASE}/&lt;slug&gt;</code> for the
              1st meeting and{' '}
              <code>{SCHEDULER_BASE}/&lt;slug&gt;/&lt;meeting-type&gt;</code>{' '}
              for each follow-up type.
            </p>
          </section>
        )}

        {advisorState.status === 'error' && (
          <section className="admin-card">
            <h2>Personal scheduling links</h2>
            <p className="admin-card-desc">
              We couldn't load your advisor record: {advisorState.message}.
              Try refreshing the page.
            </p>
          </section>
        )}

        {/* ----- Working hours ----- */}
        {advisor && (
          <WorkingHoursEditor
            advisorId={advisor.id}
            isSelf={!previewSlug}
            advisorFirstName={advisor.firstName}
          />
        )}

        {/* ----- Book on behalf launcher ----- */}
        {(advisor || user.isAdmin) && (
          <section className="admin-card">
            <h2>Book on behalf</h2>
            <p className="admin-card-desc">
              Open a guided form to book a meeting directly onto{' '}
              {advisor ? 'your' : "an advisor's"} calendar — for 2nd-5th
              meetings, application reviews, anniversary check-ins, or any
              follow-up that bypasses the public qualifying form. Same
              confirmation email + Zoom invite as a public booking.
            </p>
            <button
              type="button"
              className="bob-primary-btn"
              onClick={() => setShowBob(true)}
            >
              Book a meeting →
            </button>
          </section>
        )}

        {/* ----- Not-advisor empty state for staff accounts ----- */}
        {advisorState.status === 'not_advisor' && !user.isAdmin && (
          <section className="admin-card">
            <h2>This account isn't an advisor</h2>
            <p className="admin-card-desc">
              The /me view is for advisors who manage their own meeting
              links. Your account is provisioned for {role.toLowerCase()}{' '}
              access — head back to the{' '}
              <a href={`${PORTAL_BASE}/dashboard`}>portal dashboard</a> for
              what's available to you.
            </p>
          </section>
        )}

        {/* ----- Upcoming bookings — V3 placeholder ----- */}
        {advisor && (
          <section className="admin-card">
            <h2>
              {previewSlug
                ? `${advisor.firstName}'s upcoming bookings`
                : 'Your upcoming bookings'}
            </h2>
            <p className="admin-card-desc">
              {previewSlug
                ? `Lists every prospect routed to ${advisor.firstName} with confirmed/upcoming meetings. Lands with the operational dashboard layer.`
                : 'Lists every prospect routed to you with confirmed/upcoming meetings. Lands with the operational dashboard layer.'}
            </p>
            <p className="admin-card-note"><em>Coming in V3 — operational dashboard.</em></p>
          </section>
        )}
      </main>

      {showBob && (
        <BookOnBehalfModal
          selfAdvisor={selfAdvisorOption}
          isAdmin={user.isAdmin}
          onClose={() => setShowBob(false)}
        />
      )}
    </div>
  );
}
